/**
 * LTP.Co MASTER MOTION — approved V4 language, September 2026.
 * NATIVE FIRST → CUSTOM FALLBACK. Configured native media never receives V4 transforms.
 * Native classes are detected automatically; data-ltp-motion="native" reserves custom native media.
 * data-ltp-motion-skip opts a subtree out. Keep adjacent copy outside the native media wrapper.
 * Future sections: opt in with data-ltp-motion="text|heading|media|media-left|media-right|group".
 * data-ltp-motion="stagger" reveals direct children per visible row (never a page-long cascade).
 * data-ltp-motion="editorial-sequence" coordinates marked children in DOM order.
 * Optional: data-ltp-motion-delay="120" (ms, capped 360); data-ltp-motion-intensity="light".
 * Optional data-ltp-motion-target="img" animates media inside its existing clipping wrapper.
 * Stable LTP/Dawn component recipes below opt existing components in; never animate arbitrary DOM.
 * Keep navigation, commerce UI, individual form fields and accordion interactions STATIC.
 * animations.js alone owns hero/body-video pixels and the single PDP opening settle.
 * App wrappers wait for real content/geometry; no polling and no app-control manipulation.
 * One entrance IntersectionObserver. Existing video observers control playback, not entrance motion.
 * JS failure/no JS/reduced motion: visible content. Focus/pointer interaction completes motion first.
 * LTPMotion.refresh(section) supports new sections. Filtered collection grids deliberately do NOT replay.
 * Timing pairs below are [desktop/tablet, mobile <=749px], milliseconds or CSS pixels.
 */
(() => {
  'use strict';
  if (window.LTPMotion) return;
  const CONFIG = Object.freeze({
    threshold: 0.2, rootMargin: '0px 0px -2% 0px', mobileWidth: 749,
    rise: { small: [10, 8], medium: [18, 14], large: [24, 18] },
    duration: { text: [760, 640], heading: [790, 640], group: [650, 550], media: [900, 730], card: [650, 570] },
    scale: [1.02, 1.012], direction: [22, 14], stagger: [90, 80], maxDelay: 360,
    // Approved V4 component timing tokens: duration, distance, delay, scale, optional axis.
    roles: {
      'trust-item': [[630,550],[14,10],[0,0]],
      'trust-icon': [[560,500],[0,0],[35,35],[.92,.92]],
      'faq-heading': [[760,600],[18,14],[0,0]],
      'faq-group': [[650,550],[10,8],[80,60]],
      'story-media': [[960,760],[22,16],[0,0],[1.03,1.018],'right'],
      'story-eyebrow': [[620,520],[14,10],[110,70]],
      'story-heading': [[830,690],[24,18],[180,130]],
      'story-body': [[760,640],[18,14],[290,230]],
      'about-statement': [[750,620],[20,16],[0,0]],
      'about-video': [[950,730],[24,14],[60,50],[1.02,1.012],'left'],
      'about-heading': [[820,690],[22,18],[100,80]],
      'about-body': [[750,630],[14,12],[190,150]],
      'about-cta': [[650,570],[10,8],[280,230]],
      'brand-heading': [[790,640],[24,18],[0,0]],
      'related-heading': [[760,610],[20,14],[0,0],[1,1],'left'],
      'reviews-heading': [[760,610],[20,14],[0,0]],
      'reviews-grid': [[700,570],[12,9],[110,85]],
      'early-access': [[760,620],[20,14],[0,0],[.99,1]],
      'chapter-heading': [[500,420],[12,10],[0,0]],
      'chapter-copy': [[580,460],[12,10],[35,0]],
      'chapter-media': [[900,650],[0,0],[0,0],[1.02,1.012]]
    }
  });
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const groups = new Map();
  const triggers = new Map();
  const seen = new WeakSet();
  const waits = new Map();
  const gridVisits = new Set();
  let stopped = preference.matches || !('IntersectionObserver' in window);
  const q = (root, selector) => [...root.querySelectorAll(selector)];
  const pick = pair => pair[mobile.matches ? 1 : 0];
  const rect = element => element.getBoundingClientRect();
  const NATIVE = '.animate--zoom-in, .animate--ambient, .animate--fixed, [data-ltp-motion="native"], [data-ltp-motion-native], [data-ltp-media-target]';
  const nativeMedia = element => !!(element.closest(NATIVE) || element.querySelector(NATIVE));
  const blocked = element => !!element.closest(NATIVE + ', [data-ltp-motion-skip], header.header, .header-wrapper, .menu-drawer, footer, .footer, [data-ltp-motion="none"], .ltp-about--hero, .banner--home-video, media-gallery, .product__media-wrapper, variant-selects, product-form');
  const visible = element => { const r = rect(element); return r.width > 0 && r.height > 0; };
  const inView = element => { const r = rect(element); return r.top < window.innerHeight && r.bottom > 0 && r.left < window.innerWidth && r.right > 0; };

  function values(element, preset, extraDelay = 0) {
    const light = element.closest('[data-ltp-motion-intensity="light"]');
    const role = CONFIG.roles[element.dataset.ltpMotionRole];
    const isMedia = preset.startsWith('media');
    let duration = pick(CONFIG.duration[isMedia ? 'media' : preset] || CONFIG.duration.text);
    let distance = pick(CONFIG.rise[preset === 'heading' ? 'large' : preset === 'group' ? 'small' : 'medium']);
    let delay = 0, scale = isMedia ? pick(CONFIG.scale) : 1;
    let axis = preset === 'media-left' ? 'left' : preset === 'media-right' ? 'right' : null;
    if (isMedia) distance = axis ? pick(CONFIG.direction) : 0;
    if (preset === 'card') { distance = mobile.matches ? 12 : 16; scale = mobile.matches ? .99 : .985; }
    if (role) {
      duration = pick(role[0]); distance = pick(role[1]); delay = pick(role[2]);
      scale = role[3] ? pick(role[3]) : 1; axis = role[4];
    }
    const chapter = element.closest('.ltp-about');
    if (chapter && element.dataset.ltpMotionRole === 'chapter-copy' && !mobile.matches) {
      if (chapter.matches('.ltp-about--origin, .ltp-about--scale')) duration = 560;
      if (chapter.matches('.ltp-about--process, .ltp-about--travel')) duration = 640;
      if (chapter.matches('.ltp-about--drone')) { duration = 500; distance = 10; }
      if (chapter.matches('.ltp-about--future')) duration = 620;
    }
    if (light) { duration = Math.min(duration, mobile.matches ? 500 : 580); distance = Math.min(distance, mobile.matches ? 8 : 10); scale = isMedia ? 1.008 : 1; }
    const custom = Number(element.dataset.ltpMotionDelay);
    if (Number.isFinite(custom)) delay = custom;
    delay = Math.min(CONFIG.maxDelay, Math.max(0, delay + extraDelay));
    const x = axis && !mobile.matches ? (axis === 'left' ? -distance : distance) : 0;
    const y = axis && !mobile.matches ? 0 : distance;
    return { element, duration, delay, from: `translate3d(${x}px, ${y}px, 0) scale(${scale})` };
  }

  function detach(group) {
    const targets = triggers.get(group.trigger);
    targets?.delete(group.root);
    if (!targets?.size) { observer?.unobserve(group.trigger); triggers.delete(group.trigger); }
  }
  function finish(root) {
    const group = groups.get(root);
    if (!group) return;
    window.clearTimeout(group.timer);
    detach(group);
    group.steps.forEach(step => { step.element.dataset.ltpMotionState = 'revealed'; });
    root.dataset.ltpMotionGroupState = 'revealed';
    groups.delete(root);
  }
  function finishAll() {
    stopped = true;
    [...groups.keys()].forEach(finish);
    observer?.disconnect();
    waits.forEach(cleanup => cleanup());
    waits.clear();
  }
  function start(root) {
    const group = groups.get(root);
    if (!group || group.started) return;
    group.started = true;
    detach(group);
    if (document.hidden || stopped || preference.matches || root.contains(document.activeElement)) { finish(root); return; }
    window.requestAnimationFrame(() => {
      if (!groups.has(root)) return;
      try {
        root.dataset.ltpMotionGroupState = 'revealing';
        group.steps.forEach(step => { step.element.dataset.ltpMotionState = 'revealing'; });
        group.timer = window.setTimeout(() => finish(root), Math.max(...group.steps.map(step => step.duration + step.delay)) + 80);
      } catch { finishAll(); }
    });
  }
  const observer = stopped ? null : new IntersectionObserver(entries => {
    try {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= CONFIG.threshold) {
          [...(triggers.get(entry.target) || [])].forEach(start);
        }
      });
    } catch { finishAll(); }
  }, { threshold: CONFIG.threshold, rootMargin: CONFIG.rootMargin });

  function register(root, steps) {
    if (!root || seen.has(root) || blocked(root)) return;
    const valid = steps.filter(step => step.element?.isConnected && !blocked(step.element) && !nativeMedia(step.element));
    if (!valid.length || !visible(root)) return;
    seen.add(root);
    // Never flash already-visible content to hidden when a late script/app arrives.
    if (stopped || document.hidden || inView(root) || root.contains(document.activeElement)) return;
    let trigger = root;
    // A very tall FAQ/reviews group must still reach 20%; use its first measurable child.
    while (rect(trigger).height > window.innerHeight * 2 && trigger.firstElementChild && visible(trigger.firstElementChild)) trigger = trigger.firstElementChild;
    if (rect(trigger).height > window.innerHeight * 4) return; // Safe visible fallback, never strand a tall widget.
    const group = { root, trigger, steps: valid, started: false, timer: null };
    groups.set(root, group);
    root.dataset.ltpMotionGroupState = 'prepared';
    valid.forEach(step => {
      step.element.style.setProperty('--ltp-motion-from', step.from);
      step.element.style.setProperty('--ltp-motion-duration', `${step.duration}ms`);
      step.element.style.setProperty('--ltp-motion-delay', `${step.delay}ms`);
      step.element.dataset.ltpMotionState = 'prepared';
    });
    if (!triggers.has(trigger)) triggers.set(trigger, new Set());
    triggers.get(trigger).add(root);
    observer.observe(trigger);
  }

  function mark(element, preset, role, intensity) {
    if (!element || element.hasAttribute('data-ltp-motion') || blocked(element) || nativeMedia(element)) return;
    element.dataset.ltpMotion = preset;
    if (role) element.dataset.ltpMotionRole = role;
    if (intensity) element.dataset.ltpMotionIntensity = intensity;
  }
  function stagger(root) {
    if (seen.has(root)) return;
    seen.add(root);
    const collection = root.id === 'product-grid';
    const key = `${root.closest('.shopify-section')?.id || ''}:${root.id}`;
    if (collection && gridVisits.has(key)) return;
    if (collection) gridVisits.add(key);
    let top = null, column = 0;
    [...root.children].forEach((item, index) => {
      if (!visible(item)) return;
      const currentTop = Math.round(rect(item).top);
      if (top === null || Math.abs(currentTop - top) > 8) { column = 0; top = currentTop; }
      const trust = root.dataset.ltpMotionRole === 'trust';
      const delay = Math.min(trust ? index : column++, 3) * (trust ? (mobile.matches ? 95 : 105) : pick(CONFIG.stagger));
      if (collection) item.dataset.ltpMotionIntensity = 'light';
      if (trust) item.dataset.ltpMotionRole = 'trust-item';
      // Keep scroll-snap geometry stationary. The Places card owns the same
      // visual entrance while its snap-aligned list item remains untransformed.
      const entranceTarget = root.classList.contains('ltp-place__track') ? item.querySelector('.ltp-place__card') || item : item;
      const steps = [values(entranceTarget, trust ? 'text' : 'card', delay)];
      const icon = trust && item.querySelector('.ltp-pdp-trust__icon, .ltp-home-trust__icon');
      if (icon) { icon.dataset.ltpMotionRole = 'trust-icon'; icon.setAttribute('data-ltp-motion-icon', ''); steps.push(values(icon, 'media', delay)); }
      register(item, steps);
    });
  }
  function setup(element) {
    if (seen.has(element) || blocked(element) || element.closest('[data-ltp-motion-wait]') && !element.dataset.ltpMotionRole) return;
    const preset = element.dataset.ltpMotion;
    if (preset === 'none') return;
    if (preset === 'stagger') { stagger(element); return; }
    if (preset === 'editorial-sequence') {
      const parts = q(element, '[data-ltp-motion]').filter(part => part.closest('[data-ltp-motion="editorial-sequence"]') === element);
      register(element, parts.map((part, index) => values(part, part.dataset.ltpMotion, part.dataset.ltpMotionRole ? 0 : index * pick(CONFIG.stagger))));
      parts.forEach(part => seen.add(part));
      return;
    }
    if (element.parentElement?.closest('[data-ltp-motion="editorial-sequence"]')) return;
    const target = element.dataset.ltpMotionTarget === 'img' ? element.querySelector('img') : element;
    if (!target) return;
    const step = values(element, preset); step.element = target;
    register(element, [step]);
  }

  function waitFor(root, kind) {
    if (waits.has(root) || seen.has(root) || stopped) return;
    let mutation, resize;
    const cleanup = () => { mutation?.disconnect(); resize?.disconnect(); waits.delete(root); };
    const check = () => {
      if (!root.isConnected || stopped) { cleanup(); return true; }
      if (kind === 'recommendations') {
        const cards = root.querySelector('.product-grid');
        if (!cards?.children.length || !visible(cards)) return false;
        cleanup(); seen.add(root);
        mark(root.querySelector('.related-products__heading'), 'heading', 'related-heading');
        mark(cards, 'stagger'); scan(root); return true;
      }
      let content, ready;
      if (kind === 'reviews') {
        content = root.querySelector('[data-widget-container="reviews-grid-widget"], [data-widget="reviews-grid"]');
        ready = content?.querySelector('.jdgm-review-card');
      } else if (kind === 'testimonials') {
        content = root.querySelector('[data-widget-container], .jdgm-widget');
        ready = content?.querySelector('.jdgm-review-card, .jdgm-carousel-item, [data-review-id], blockquote') ||
          (content?.textContent.trim().length > 80 && content.querySelector('button'));
      } else {
        content = root.classList.contains('ltp-pdp-early-access') ? root.firstElementChild : root;
        ready = content?.querySelector('form, input[type="email"]');
      }
      if (!ready || !content || rect(content).height < 80) return false;
      cleanup();
      if (content !== root) seen.add(root);
      content.dataset.ltpMotionRole = kind === 'reviews' ? 'reviews-grid' : 'early-access';
      register(content, [values(content, 'group')]);
      return true;
    };
    const safeCheck = () => { try { check(); } catch { cleanup(); finishAll(); } };
    if (check()) return;
    mutation = new MutationObserver(safeCheck);
    mutation.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class','style','hidden'] });
    if ('ResizeObserver' in window) { resize = new ResizeObserver(safeCheck); resize.observe(root); }
    waits.set(root, cleanup);
  }

  // Stable shared-component adapters. Future custom sections should prefer explicit data attributes.
  function components(root) {
    const each = (selector, preset, role, intensity) => q(root, selector).forEach(el => mark(el, preset, role, intensity));
    each('.ltp-video-feature__content h2', 'heading', 'brand-heading');
    each('.ltp-contact form', 'group', null, 'light');
    each('.collection__title, .collection-list-title, .collection-list-wrapper .title-wrapper-with-link', 'heading');
    each('.collection .product-grid, .collection-list', 'stagger');
    each('.collection-hero__text-wrapper, .collection-editorial__content', 'group', null, 'light');
    each('.collection-editorial__media', 'media', null, 'light');
    each('#product-grid', 'stagger', null, 'light');
    each('.rich-text__blocks, .image-with-text__content', 'group', null, 'light');
    each('.image-with-text__media', 'media', null, 'light');
    each('.collapsible-content__header, .main-page-title', 'heading', null, 'light');
    each('.collapsible-content__grid, .newsletter__wrapper', 'group', null, 'light');
    each('.multicolumn .title-wrapper-with-link', 'heading', null, 'light');
    each('.multicolumn-list', 'stagger', null, 'light');
    // Audited editorial/support components only, never arbitrary page HTML.
    each('.ltp-ea__proof-copy, .ltp-ea__destinations, .ltp-ea__benefits, .ltp-ea__story-copy, .ltp-ea__collection-intro, .ltp-ea__pair-heading, .ltp-ea__pair-index-copy, .ltp-ea__collection-index-copy, .ltp-ea__shop-intro, .ltp-ea__confidence, .ltp-ea__final', 'group', null, 'light');
    each('.ltp-ea__story-media, .ltp-ea__editorial-image', 'media', null, 'light');
    each('.ltp-ea__pair-grid, .ltp-ea__artwork-grid, .ltp-ea__image-duo, .ltp-ea__path-grid', 'stagger', null, 'light');
    each('.ltp-ea__pair-list, .ltp-ea__artwork-list', 'group', null, 'light');
    each('.main-blog .title--primary, .blog__title, .article-template__title, .collage-wrapper-title, .video-section .title-wrapper, .contact .title', 'heading', null, 'light');
    each('.blog-articles, .blog__posts, .collage', 'stagger', null, 'light');
    each('.article-template__content, .contact form', 'group', null, 'light');
    // Deferred/player-controlled video stays static; never slide its controls.
    q(root, '.banner__media').forEach(media => {
      if (media.closest('slideshow-component')) return;
      mark(media, 'media', null, 'light');
      if (media.dataset.ltpMotion === 'media' && !media.dataset.ltpMotionTarget) media.dataset.ltpMotionTarget = 'img';
    });
    // Search controls remain static; the existing result-grid recipe supplies light card entrances.
    // Support-page hero headings only. The homepage hero and PDP gallery stay untouched.
    if (document.body.dataset.ltpMotionPage === 'page') each('.banner__heading', 'heading', null, 'light');
    q(root, 'product-recommendations.related-products').forEach(el => waitFor(el, 'recommendations'));
    q(root, '[data-ltp-motion-wait]').forEach(el => waitFor(el, el.dataset.ltpMotionWait));
  }
  function excludeNativePresets(root) {
    const candidates = q(root, '[data-ltp-motion]');
    if (root.matches?.('[data-ltp-motion]')) candidates.unshift(root);
    candidates.forEach(element => {
      const preset = element.dataset.ltpMotion;
      if (preset === 'native' || preset === 'none') return;
      const coordinated = preset === 'editorial-sequence' || preset === 'stagger';
      if (!element.closest(NATIVE) && (coordinated || !element.querySelector(NATIVE))) return;
      finish(element);
      ['ltp-motion', 'ltp-motion-role', 'ltp-motion-target', 'ltp-motion-delay', 'ltp-motion-intensity',
        'ltp-motion-state', 'ltp-motion-group-state'].forEach(name => element.removeAttribute('data-' + name));
      ['from', 'duration', 'delay'].forEach(name => element.style.removeProperty('--ltp-motion-' + name));
    });
  }
  function scan(root = document) {
    if (stopped) return;
    try {
      excludeNativePresets(root);
      components(root);
      if (root.matches?.('[data-ltp-motion]')) setup(root);
      q(root, '[data-ltp-motion]').forEach(setup);
    } catch { finishAll(); }
  }
  function release(event) {
    [...groups].forEach(([root, group]) => {
      if (root.contains(event.target) || group.steps.some(step => step.element.contains(event.target))) finish(root);
    });
  }
  preference.addEventListener('change', () => { if (preference.matches) finishAll(); });
  // Resizing mid-entrance should never leave old-direction transforms or focused controls moving.
  mobile.addEventListener('change', () => [...groups.keys()].forEach(finish));
  document.addEventListener('focusin', release, true);
  document.addEventListener('pointerdown', release, true);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) [...groups].forEach(([root, group]) => { if (group.started) finish(root); });
  });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...groups.keys()].forEach(root => { if (event.target.contains(root)) finish(root); });
    [...waits].forEach(([root, cleanup]) => { if (event.target.contains(root)) cleanup(); });
  });
  document.addEventListener('shopify:section:select', event => {
    [...groups.keys()].forEach(root => { if (event.target.contains(root)) finish(root); });
  });
  window.addEventListener('pagehide', finishAll, { once: true });
  window.LTPMotion = Object.freeze({
    version: '4.0', config: CONFIG, refresh: scan,
    status: () => ({ reduced: preference.matches, stopped, pending: groups.size, waiting: waits.size,
      groups: [...groups].map(([root, group]) => ({ name: root.dataset.ltpMotionRole || root.dataset.ltpMotion || root.className,
        state: root.dataset.ltpMotionGroupState, steps: group.steps.map(({duration,delay,from}) => ({duration,delay,from})) })) })
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), {once:true});
  else scan();
})();
